// сведения о специалистах
import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import {
  CFormLabel,
  CFormInput,
  CForm,
  CContainer,
  CRow,
  CCol,
  CButton,
  CTable,
  CTableHead,
  CTableBody,
  CTableRow,
  CTableHeaderCell,
  CTableDataCell,
  CModal,
  CModalHeader,
  CModalTitle,
  CModalBody,
  CFormSelect
} from '@coreui/react';
import PropTypes from 'prop-types';
import CIcon from '@coreui/icons-react';
import { cilStar, cilPlus, cilMinus } from '@coreui/icons';

const SpecialistsInfoTab = forwardRef(({ initialData, onSave }, ref) => {
  const [specialists, setSpecialists] = useState([]);
  const [selectedSpecialistId, setSelectedSpecialistId] = useState(null);
  
  // Модальное окно для справочника специалистов
  const [showModal, setShowModal] = useState(false);
  const [specialistsData, setSpecialistsData] = useState([]);
  const [selectedModalSpecialist, setSelectedModalSpecialist] = useState(null);
  const [loading, setLoading] = useState(false);

  // Справочники для выпадающих списков
  const positions = [
    'Инженер-строитель',
    'Техник-строитель',
    'Главный специалист',
    'Руководитель группы',
    'Эксперт'
  ];

  const workTypes = [
    'Обследование конструкций',
    'Лабораторные испытания',
    'Расчеты и анализ',
    'Составление отчета',
    'Визуальный осмотр'
  ];

  useEffect(() => {
    if (initialData) {
      setSpecialists(initialData.specialists || []);
    }
  }, [initialData]);

  // Загрузка данных специалистов из API (заглушка)
  const fetchSpecialistsData = async () => {
    setLoading(true);
    try {
      // Заглушка для имитации загрузки данных
      setTimeout(() => {
        const mockData = [
          { ID: 1, Name: 'Иванов Иван Иванович', Position: 'Инженер-строитель' },
          { ID: 2, Name: 'Петров Петр Петрович', Position: 'Техник-строитель' },
          { ID: 3, Name: 'Сидорова Анна Владимировна', Position: 'Главный специалист' },
          { ID: 4, Name: 'Кузнецов Дмитрий Сергеевич', Position: 'Руководитель группы' },
          { ID: 5, Name: 'Смирнова Ольга Александровна', Position: 'Эксперт' }
        ];
        setSpecialistsData(mockData);
        setLoading(false);
      }, 500);
    } catch (error) {
      console.error('Ошибка загрузки данных специалистов:', error);
      setSpecialistsData([]);
      setLoading(false);
    }
  };

  const handleOpenModal = () => {
    setShowModal(true);
    setSelectedModalSpecialist(null);
    fetchSpecialistsData();
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedModalSpecialist(null);
  };

  const handleSpecialistDoubleClick = (specialist) => {
    const newId = Math.max(...specialists.map(item => item.id), 0) + 1;
    const newNumber = specialists.length + 1;
    
    const newSpecialist = {
      id: newId,
      number: newNumber,
      name: specialist.Name,
      position: specialist.Position || '',
      certificate: '',
      workType: ''
    };
    
    const updatedSpecialists = [...specialists, newSpecialist];
    setSpecialists(updatedSpecialists);
    setSelectedSpecialistId(newId);
    handleCloseModal();
  };

  const handleSpecialistClick = (id) => {
    setSelectedSpecialistId(id);
  };

  const addSpecialist = () => {
    handleOpenModal();
  };

  const removeSpecialist = () => {
    if (selectedSpecialistId && specialists.length > 0) {
      const confirmDelete = window.confirm('Удалить выбранного специалиста?');
      
      if (confirmDelete) {
        const updatedSpecialists = specialists.filter(item => item.id !== selectedSpecialistId);
        
        // Перенумеровываем оставшихся специалистов
        const renumberedSpecialists = updatedSpecialists.map((item, index) => ({
          ...item,
          number: index + 1
        }));
        
        setSpecialists(renumberedSpecialists);
        setSelectedSpecialistId(renumberedSpecialists.length > 0 ? renumberedSpecialists[0].id : null);
      }
    } else if (specialists.length > 0) {
      const confirmDelete = window.confirm('Удалить последнего специалиста?');
      
      if (confirmDelete) {
        const updatedSpecialists = specialists.slice(0, -1);
        
        // Перенумеровываем оставшихся специалистов
        const renumberedSpecialists = updatedSpecialists.map((item, index) => ({
          ...item,
          number: index + 1
        }));
        
        setSpecialists(renumberedSpecialists);
        setSelectedSpecialistId(renumberedSpecialists.length > 0 ? renumberedSpecialists[renumberedSpecialists.length - 1].id : null);
      }
    }
  };

  const markAsMain = () => {
    if (selectedSpecialistId) {
      setSpecialists(specialists.map(item => ({
        ...item,
        isMain: item.id === selectedSpecialistId
      })));
    }
  };

  const handleFieldChange = (id, field, value) => {
    setSpecialists(specialists.map(item => 
      item.id === id ? { ...item, [field]: value } : item
    ));
  };

  const getFormDataForSave = () => {
    return {
      specialists
    };
  };

  useImperativeHandle(ref, () => ({
    save: async () => {
      if (onSave) {
        const formData = getFormDataForSave();
        await onSave(formData);
      }
    },
    getFormData: getFormDataForSave
  }));

  return (
    <CContainer fluid className="specialists-info-tab-container">
      <CForm>
        {/* Заголовок */}
        <CRow className="mb-3">
          <CCol>
            <CFormLabel className="fw-bold">
              Обследование строительных конструкций и оформление отчетной документации проводилась следующими специалистами:
            </CFormLabel>
            <div className="border-bottom mt-2 mb-3"></div>
          </CCol>
        </CRow>

        {/* Кнопки управления */}
        <CRow className="mb-3">
          <CCol>
            <div className="d-flex gap-2">
              {/* <CButton 
                color="light" 
                variant="outline" 
                size="sm" 
                className="text-dark border"
                onClick={addSpecialist}
              >
                <CIcon icon={cilPlus} className="me-1" />
              </CButton> */}
              <CButton 
                color="light" 
                variant="outline" 
                size="sm" 
                className="text-dark border"
                onClick={removeSpecialist}
              >
                <CIcon icon={cilMinus} className="me-1" />
              </CButton>
              <CButton 
                color="light" 
                variant="outline" 
                size="sm" 
                className="text-dark border"
                onClick={markAsMain}
              >
                <CIcon icon={cilStar} className="me-1" />
              </CButton>
            </div>
          </CCol>
        </CRow>

        {/* Таблица специалистов */}
        <CRow className="mb-3">
          <CCol>
            <CTable bordered small className="mb-0">
              <CTableHead>
                <CTableRow>
                  <CTableHeaderCell className="border text-center" style={{ width: '50px' }}>№</CTableHeaderCell>
                  <CTableHeaderCell className="border text-center" style={{ width: '250px' }}>Исполнитель</CTableHeaderCell>
                  <CTableHeaderCell className="border text-center" style={{ width: '200px' }}>Должность</CTableHeaderCell>
                  <CTableHeaderCell className="border text-center" style={{ width: '150px' }}>Удостоверение</CTableHeaderCell>
                  <CTableHeaderCell className="border text-center">Вид работы</CTableHeaderCell>
                </CTableRow>
              </CTableHead>
              <CTableBody>
                {specialists.map((specialist) => (
                  <CTableRow 
                    key={specialist.id} 
                    onClick={() => handleSpecialistClick(specialist.id)}
                    className={selectedSpecialistId === specialist.id ? 'table-primary' : ''}
                    style={{ cursor: 'pointer' }}
                  >
                    <CTableDataCell className="border text-center" style={{ verticalAlign: 'middle' }}>
                      <div className="d-flex align-items-center justify-content-center">
                        {specialist.isMain && (
                          <CIcon icon={cilStar} className="text-warning me-1" size="sm" />
                        )}
                        {specialist.number}
                      </div>
                    </CTableDataCell>
                    <CTableDataCell className="border p-0">
                      <CFormInput
                        type="text"
                        value={specialist.name}
                        onChange={(e) => handleFieldChange(specialist.id, 'name', e.target.value)}
                        placeholder="ФИО исполнителя"
                        className="border-0 w-100"
                        style={{ borderRadius: '0' }}
                      />
                    </CTableDataCell>
                    <CTableDataCell className="border p-0">
                      <CFormSelect
                        value={specialist.position}
                        onChange={(e) => handleFieldChange(specialist.id, 'position', e.target.value)}
                        className="border-0 w-100"
                        style={{ borderRadius: '0' }}
                      >
                        <option value="">Выберите должность</option>
                        {positions.map((position, index) => (
                          <option key={index} value={position}>{position}</option>
                        ))}
                      </CFormSelect>
                    </CTableDataCell>
                    <CTableDataCell className="border p-0">
                      <CFormInput
                        type="text"
                        value={specialist.certificate}
                        onChange={(e) => handleFieldChange(specialist.id, 'certificate', e.target.value)}
                        placeholder="Номер удостоверения"
                        className="border-0 w-100"
                        style={{ borderRadius: '0' }}
                      />
                    </CTableDataCell>
                    <CTableDataCell className="border p-0">
                      <CFormSelect
                        value={specialist.workType}
                        onChange={(e) => handleFieldChange(specialist.id, 'workType', e.target.value)}
                        className="border-0 w-100"
                        style={{ borderRadius: '0' }}
                      >
                        <option value="">Выберите вид работы</option>
                        {workTypes.map((workType, index) => (
                          <option key={index} value={workType}>{workType}</option>
                        ))}
                      </CFormSelect>
                    </CTableDataCell>
                  </CTableRow>
                ))}
                {specialists.length === 0 && (
                  <CTableRow>
                    <CTableDataCell 
                      colSpan="5" 
                      className="border text-center text-muted py-3"
                    >
                      Нет добавленных специалистов. Нажмите "+" чтобы добавить.
                    </CTableDataCell>
                  </CTableRow>
                )}
              </CTableBody>
            </CTable>
          </CCol>
        </CRow>
      </CForm>

      {/* Модальное окно справочника специалистов */}
      <CModal visible={showModal} onClose={handleCloseModal} size="lg">
        <CModalHeader>
          <CModalTitle>Справочник: Специалисты</CModalTitle>
        </CModalHeader>
        <CModalBody>
          {loading ? (
            <div className="text-center">Загрузка данных специалистов...</div>
          ) : (
            <>
              <CTable bordered small className="mb-3">
                <CTableHead>
                  <CTableRow>
                    <CTableHeaderCell className="border">ФИО</CTableHeaderCell>
                    <CTableHeaderCell className="border">Должность</CTableHeaderCell>
                  </CTableRow>
                </CTableHead>
                <CTableBody>
                  {specialistsData.map((specialist, index) => (
                    <CTableRow 
                      key={specialist.ID || index}
                      onDoubleClick={() => handleSpecialistDoubleClick(specialist)}
                      onClick={() => setSelectedModalSpecialist(specialist)}
                      className={selectedModalSpecialist?.ID === specialist.ID ? 'table-primary' : ''}
                      style={{ cursor: 'pointer' }}
                    >
                      <CTableDataCell className="border">{specialist.Name}</CTableDataCell>
                      <CTableDataCell className="border">{specialist.Position}</CTableDataCell>
                    </CTableRow>
                  ))}
                </CTableBody>
              </CTable>
              <div className="text-muted small">
                💡 Для добавления: двойной клик по строке
              </div>
            </>
          )}
        </CModalBody>
      </CModal>
    </CContainer>
  );
});

SpecialistsInfoTab.propTypes = {
  initialData: PropTypes.shape({
    specialists: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number.isRequired,
        number: PropTypes.number.isRequired,
        name: PropTypes.string.isRequired,
        position: PropTypes.string,
        certificate: PropTypes.string,
        workType: PropTypes.string,
        isMain: PropTypes.bool
      })
    )
  }),
  onSave: PropTypes.func
};

SpecialistsInfoTab.defaultProps = {
  initialData: null,
  onSave: null
};

export default SpecialistsInfoTab;